import { apiDRSGet } from "@src/apis/drs-get";
import { apiDRSUpdateSection } from "@src/apis/drs-update-section";
import { apiSprayBinderTypeDepotSearch } from "@src/apis/spray-binder-type-depot-search";
import { Attachment } from "@src/types/Attachment";
import { DRS, DRSSection } from "@src/types/drs/DRS";
import _set from "lodash/set";
import { useEffect, useRef, useState } from "react";
import { DRSPageType, DRSSectionName, mapDRSPageTypeToRoleId } from "./DRSPage";
import { useDRSStore } from "./DRSStore";

interface ReturnType {
  drs: DRS | null;
  drsId: number;
  drsPageType: DRSPageType;
  loading: boolean;
  resetDRS(): void;
  fetchDRS(id: string, type: DRSPageType): void;
  refresh(): void;
  updateViaPath(path: string, data: any): void;
  updateSectionViaPath(options: {
    sectionName: DRSSectionName;
    path: string;
    data: any;
  }): void;
  attachments: Attachment[];
  setAttachments(attachments: Attachment[]): void;
}

export function useDRS(): ReturnType {
  const {
    drs,
    setDRS,
    setId,
    id: drsId,
    drsPageType,
    setDRSPageType,
    attachments,
    setAttachments,
  } = useDRSStore();
  const [loading, setLoading] = useState<boolean>(false);
  const isActiveRef = useRef<boolean>(false);
  const lastFetchParamsRef = useRef<{id: string, type: DRSPageType} | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      isActiveRef.current = false;
    };
  }, []);
  
  const resetdrs = () => {
    setDRS(null);
  };
  
  const fetch = async (id: string, type: DRSPageType, forceRefresh: boolean = false) => {
    try {
      // Check if component is unmounted
      if (!isMountedRef.current) {
        return;
      }

      // Check if we're already fetching the same data (but allow force refresh)
      const currentParams = {id, type};
      if (isActiveRef.current) {
        return;
      }
      
      // Only block duplicate requests if it's not a force refresh
      if (!forceRefresh && 
          lastFetchParamsRef.current && 
          lastFetchParamsRef.current.id === id && 
          lastFetchParamsRef.current.type === type) {
        return;
      }

      if (DRSPageType[type]) {
        isActiveRef.current = true;
        lastFetchParamsRef.current = currentParams;
        
        if (isMountedRef.current) {
          setLoading(true);
        }
        
        // Pre-load spray depot data for spray seal projects
        try {
          const sprayDepotResult = await apiSprayBinderTypeDepotSearch({ query: '' })
          const depots = sprayDepotResult.data.spraybindertypedepots
          // Store in global cache for use throughout the app
          ;(window as any).__sprayDepotCache = depots
        } catch (error) {
          console.warn('Failed to load spray depot data:', error)
          // Continue with DRS fetch even if spray depot fails
        }
        
        const result = await apiDRSGet({
          drsId: id,
          roleId: mapDRSPageTypeToRoleId(type),
        });

        // Check if component is still mounted before updating state
        if (!isMountedRef.current) {
          isActiveRef.current = false;
          return;
        }

        // Log the mobileAssets data to understand its structure
        console.log("result ---->", result.data.drs.mobileAssets);

        // Access the assets array under spraySeal
        const spraySealAssets =
          result.data.drs.mobileAssets.boral.spraySeal.assets;

        // Check if the array exists and contains data
        if (spraySealAssets && spraySealAssets.length > 0) {
          spraySealAssets.forEach((asset) => {
            console.log("fileLocation:", asset.fileLocation ?? '');
            console.log("cpFileName:", asset.cpFileName ?? '');
          });
        } else {
          console.log("No assets found in spraySeal.");
        }

        setDRS(result.data.drs);
        setId(Number(id));
        setDRSPageType(type);
        setLoading(false);
        isActiveRef.current = false;
      }
    } catch (e) {
      if (isMountedRef.current) {
        setLoading(false);
      }
      isActiveRef.current = false;
    }
  };
  const refresh = async () => {
    // Clear the cache and force a fresh fetch
    lastFetchParamsRef.current = null;
    return fetch(drsId + "", drsPageType, true);
  };
  const updateViaPath = async (path: string, data: any) => {};
  const updateSectionViaPath = async ({
    sectionName,
    path,
    data,
  }: {
    sectionName: DRSSectionName;
    path: string;
    data: any;
  }) => {
    if (drs === null) {
      return;
    }
    try {
      const sectionData = drs[
        transformSectionNameToDataSectionName(sectionName)
      ] as DRSSection;
      let finalData = Object.assign({}, sectionData);
      _set(finalData, path, data);
      console.log(path, data);
      
      // Update local state immediately before API call
      const updatedDRS = { ...drs } as DRS;
      const sectionKey = transformSectionNameToDataSectionName(sectionName);
      (updatedDRS as any)[sectionKey] = finalData;
      setDRS(updatedDRS);
      
      await apiDRSUpdateSection({
        sectionName: transformSectionNameToApiSectionName(sectionName),
        drsId: drs.jobDetails.id + "",
        roleId: mapDRSPageTypeToRoleId(drsPageType),
        data: finalData,
      });
    } catch (e) {
      // If API call fails, revert the local state by refreshing
      await refresh();
      throw e;
    }
  };
  return {
    drs,
    drsId: drsId!,
    drsPageType,
    loading,
    resetDRS: resetdrs,
    fetchDRS: fetch,
    updateViaPath,
    updateSectionViaPath,
    refresh,
    attachments,
    setAttachments,
  };
}

export function transformSectionNameToDataSectionName(
  sectionName: DRSSectionName
): keyof DRS {
  switch (sectionName) {
    case DRSSectionName.Job:
      return "jobDetails";
    case DRSSectionName.Rework:
      return "rework";
    case DRSSectionName.Material:
      return "material";
    case DRSSectionName.Transport:
      return "transport";
    case DRSSectionName.MobileAssets:
      return "mobileAssets";
    case DRSSectionName.Subcontractors:
      return "subcontractor";
    case DRSSectionName.Labour:
      return "labour";
    case DRSSectionName.Quality:
      return "qa";
    case DRSSectionName.Summary:
      return "summary";
  }
}
function transformSectionNameToApiSectionName(
  sectionName: DRSSectionName
): string {
  switch (sectionName) {
    case DRSSectionName.Job:
      return "jobDetails";
    case DRSSectionName.Rework:
      return "rework";
    case DRSSectionName.Material:
      return "materials";
    case DRSSectionName.Transport:
      return "transport";
    case DRSSectionName.MobileAssets:
      return "mobileAssets";
    case DRSSectionName.Subcontractors:
      return "any";
    case DRSSectionName.Labour:
      return "labour";
    case DRSSectionName.Quality:
      return "qaRequirements";
    case DRSSectionName.Summary:
      return "summary";
  }
}
